import { useState, useRef, useEffect } from 'react';
import type { AdAnimation } from '../types';
import { downloadFile } from '@/lib/download';

interface Props {
  onBack: () => void;
  onStartNew?: () => void;
  onAddText: () => void;
  onAddButton: () => void;
  onAddShape: () => void;
  onAddLogo: (file: File) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  animation: AdAnimation;
  onAnimationChange: (a: AdAnimation) => void;
  imageUrl: string;
  onImageChange: (file: File) => void;
  onExportWithOverlays?: () => Promise<void>;
  isExporting?: boolean;
  exportError?: string | null;
  onSave?: () => void;
  isSaving?: boolean;
  saveLabel?: string;
  onExportHtml?: () => void;
  htmlCopied?: boolean;
  onExportVideo?: () => void;
  isExportingVideo?: boolean;
  videoProgress?: number;
  onErase?: () => void;
}

const ANIMATION_OPTIONS: { id: AdAnimation; label: string; icon: string }[] = [
  { id: 'none',        label: 'No animation',  icon: 'ri-stop-circle-line' },
  { id: 'ken-burns',   label: 'Ken Burns',     icon: 'ri-expand-diagonal-line' },
  { id: 'cta-pulse',   label: 'CTA Pulse',     icon: 'ri-pulse-line' },
  { id: 'spotlight',   label: 'Spotlight',     icon: 'ri-flashlight-line' },
  { id: 'text-reveal', label: 'Text Reveal',   icon: 'ri-text-direction-l' },
];

export default function EditorToolbar({
  onBack,
  onStartNew,
  onAddText,
  onAddButton,
  onAddShape,
  onAddLogo,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  animation,
  onAnimationChange,
  imageUrl,
  onImageChange,
  onExportWithOverlays,
  isExporting = false,
  exportError,
  onSave,
  isSaving = false,
  saveLabel,
  onExportHtml,
  htmlCopied = false,
  onExportVideo,
  isExportingVideo = false,
  videoProgress = 0,
  onErase,
}: Props) {
  const [animOpen, setAnimOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [mobileAddOpen, setMobileAddOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [showNewConfirm, setShowNewConfirm] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const desktopAddRef = useRef<HTMLDivElement>(null);
  const mobileAddRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<HTMLDivElement>(null);
  const downloadRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (desktopAddRef.current && !desktopAddRef.current.contains(e.target as Node)) setAddOpen(false);
      if (mobileAddRef.current && !mobileAddRef.current.contains(e.target as Node)) setMobileAddOpen(false);
      if (animRef.current && !animRef.current.contains(e.target as Node)) setAnimOpen(false);
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) setDownloadOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleExportHtmlClick = () => { onExportHtml?.(); setDownloadOpen(false); setMoreOpen(false); };
  const handleExportVideoClick = () => { onExportVideo?.(); setDownloadOpen(false); setMoreOpen(false); };
  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onImageChange(file);
    e.target.value = '';
  };
  const handleLogoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onAddLogo(file);
    e.target.value = '';
  };
  const handleDownloadClean = () => { downloadFile(imageUrl, `ad-clean-${Date.now()}.jpg`); setDownloadOpen(false); setMoreOpen(false); };
  const handleExportOverlays = async () => {
    if (!onExportWithOverlays) return;
    setDownloadOpen(false);
    setMoreOpen(false);
    await onExportWithOverlays();
  };

  const activeAnim = ANIMATION_OPTIONS.find((a) => a.id === animation);
  const saveText = saveLabel ?? (isSaving ? 'Saving…' : 'Save');

  return (
    <div className="shrink-0 z-10">
      {/* Hidden file inputs */}
      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageInputChange} />
      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoInputChange} />

      {/* ── Toolbar bar ─────────────────────────────────────────── */}
      <div className="h-14 bg-stone-900 border-b border-stone-800 flex items-center px-3 gap-1.5 md:gap-2 md:px-4">

        {/* Back */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-stone-400 hover:text-stone-200 hover:bg-stone-800 transition-colors cursor-pointer text-sm font-medium whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          <span className="hidden sm:inline">Back</span>
        </button>

        <div className="w-px h-6 bg-stone-800" />

        {/* Title */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-amber-400">
            <i className="ri-edit-2-fill text-white text-xs" />
          </div>
          <span className="text-sm font-bold text-stone-200 whitespace-nowrap hidden sm:inline">Ad Editor</span>
        </div>

        {/* ── Desktop-only controls ─────────────────────────────── */}
        <div className="hidden md:flex items-center gap-1.5 flex-1">
          <div className="w-px h-6 bg-stone-800 mx-1" />

          {/* Start New */}
          {onStartNew && (
            <div className="relative">
              <button
                onClick={() => setShowNewConfirm((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 border border-stone-700 hover:border-stone-500 hover:bg-stone-700 text-stone-400 hover:text-stone-200 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-restart-line text-sm" />
                Start New
              </button>
              {showNewConfirm && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-stone-800 border border-stone-700 rounded-xl p-4 shadow-2xl z-50">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/20 shrink-0">
                      <i className="ri-alert-line text-amber-400 text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white leading-snug">Start a new project?</p>
                      <p className="text-xs text-stone-400 mt-1 leading-relaxed">This will clear the canvas and all elements. Your saved projects won&apos;t be affected.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setShowNewConfirm(false); onStartNew(); }} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-restart-line" /> Yes, start new
                    </button>
                    <button onClick={() => setShowNewConfirm(false)} className="flex-1 py-2 rounded-lg bg-stone-700 hover:bg-stone-600 text-stone-300 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="w-px h-6 bg-stone-800 mx-1" />

          {/* Add elements dropdown — DESKTOP */}
          <div className="relative" ref={desktopAddRef}>
            <button
              onClick={() => setAddOpen((o) => !o)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Add
              <i className={`${addOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} />
            </button>
            {addOpen && (
              <div className="absolute top-full left-0 mt-1.5 w-44 bg-stone-800 border border-stone-700 rounded-xl overflow-hidden shadow-xl z-50">
                <button onClick={() => { onAddText(); setAddOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-text text-orange-400" /> Text Block
                </button>
                <button onClick={() => { onAddButton(); setAddOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-cursor-fill text-orange-400" /> CTA Button
                </button>
                <button onClick={() => { onAddShape(); setAddOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-square-line text-orange-400" /> Shape / Box
                </button>
                <div className="h-px bg-stone-700 mx-3" />
                <button onClick={() => { setAddOpen(false); setTimeout(() => logoInputRef.current?.click(), 0); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-image-add-line text-orange-400" /> Logo / Image
                </button>
              </div>
            )}
          </div>

          <div className="w-px h-6 bg-stone-800 mx-1" />

          {/* Undo / Redo */}
          <div className="flex items-center gap-1">
            <button onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)" className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${canUndo ? 'text-stone-400 hover:bg-stone-800 hover:text-stone-200 cursor-pointer' : 'text-stone-700 cursor-not-allowed'}`}>
              <i className="ri-arrow-go-back-line text-base" />
            </button>
            <button onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)" className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${canRedo ? 'text-stone-400 hover:bg-stone-800 hover:text-stone-200 cursor-pointer' : 'text-stone-700 cursor-not-allowed'}`}>
              <i className="ri-arrow-go-forward-line text-base" />
            </button>
          </div>

          <div className="flex-1" />

          {/* Save */}
          {onSave && (
            <>
              <button
                onClick={onSave}
                disabled={isSaving}
                title="Save (Ctrl+S)"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${isSaving ? 'bg-stone-800 border border-stone-700 text-stone-500 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
              >
                {isSaving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : <><i className="ri-save-3-line" /> {saveText}</>}
              </button>
              <div className="w-px h-6 bg-stone-800 mx-1" />
            </>
          )}

          {/* Animation picker */}
          <div className="relative" ref={animRef}>
            <button
              onClick={() => setAnimOpen((o) => !o)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${animation !== 'none' ? 'bg-orange-500/20 border-orange-500/40 text-orange-300' : 'bg-stone-800 border-stone-700 text-stone-400 hover:border-stone-600 hover:text-stone-300'}`}
            >
              <i className={ANIMATION_OPTIONS.find((a) => a.id === animation)?.icon ?? 'ri-movie-line'} />
              {ANIMATION_OPTIONS.find((a) => a.id === animation)?.label ?? 'Animation'}
              <i className={`${animOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} />
            </button>
            {animOpen && (
              <div className="absolute top-full right-0 mt-1.5 w-48 bg-stone-800 border border-stone-700 rounded-xl overflow-hidden shadow-xl z-50">
                {ANIMATION_OPTIONS.map((opt) => (
                  <button key={opt.id} onClick={() => { onAnimationChange(opt.id); setAnimOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-xs transition-colors cursor-pointer ${animation === opt.id ? 'bg-orange-500/20 text-orange-300' : 'text-stone-300 hover:bg-stone-700 hover:text-white'}`}>
                    <i className={`${opt.icon} text-sm ${animation === opt.id ? 'text-orange-400' : 'text-stone-500'}`} />
                    {opt.label}
                    {animation === opt.id && <i className="ri-check-line text-orange-400 ml-auto" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-6 bg-stone-800 mx-1" />

          {/* Change image */}
          <button
            onClick={() => imageInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 border border-stone-700 hover:border-orange-500/50 hover:bg-stone-700 text-stone-300 hover:text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-image-edit-line text-orange-400" />
            Change Image
          </button>

          {/* Erase tool */}
          {onErase && (
            <button
              onClick={onErase}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 border border-stone-700 hover:border-orange-500/50 hover:bg-stone-700 text-stone-300 hover:text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-eraser-line text-orange-400" />
              Erase
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold leading-none">Beta</span>
            </button>
          )}

          {/* Download dropdown */}
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setDownloadOpen((o) => !o)}
              disabled={isExporting}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${isExporting ? 'bg-stone-800 border-stone-700 text-stone-500 cursor-not-allowed' : 'bg-stone-800 border-stone-700 hover:border-stone-600 hover:bg-stone-700 text-stone-300'}`}
            >
              {isExporting ? <><i className="ri-loader-4-line animate-spin" /> Exporting...</> : <><i className="ri-download-2-line" /> Download <i className={`${downloadOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} /></>}
            </button>
            {downloadOpen && !isExporting && (
              <div className="absolute top-full right-0 mt-1.5 w-56 bg-stone-800 border border-stone-700 rounded-xl overflow-hidden shadow-xl z-50">
                <button onClick={handleExportVideoClick} disabled={isExportingVideo} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-stone-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500/20 text-orange-400 shrink-0 mt-0.5">
                    <i className={`${isExportingVideo ? 'ri-loader-4-line animate-spin' : 'ri-film-line'} text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-stone-200 whitespace-nowrap">{isExportingVideo ? `Rendering… ${Math.round(videoProgress * 100)}%` : 'Export Animated Video'}</p>
                    <p className="text-xs text-stone-500 leading-snug mt-0.5">Downloads as .webm with animations</p>
                  </div>
                </button>
                <div className="h-px bg-stone-700 mx-3" />
                <button onClick={handleExportOverlays} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-stone-700 transition-colors cursor-pointer">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500/20 text-orange-400 shrink-0 mt-0.5">
                    <i className="ri-image-2-line text-sm" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-stone-200 whitespace-nowrap">Export PNG with Overlays</p>
                    <p className="text-xs text-stone-500 leading-snug mt-0.5">Text, buttons &amp; effects included</p>
                  </div>
                </button>
                <div className="h-px bg-stone-700 mx-3" />
                <button onClick={handleExportHtmlClick} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-stone-700 transition-colors cursor-pointer">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${htmlCopied ? 'bg-green-500/20 text-green-400' : 'bg-stone-700 text-stone-400'}`}>
                    <i className={`${htmlCopied ? 'ri-check-line' : 'ri-code-s-slash-line'} text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-bold whitespace-nowrap ${htmlCopied ? 'text-green-300' : 'text-stone-200'}`}>{htmlCopied ? 'Copied!' : 'Copy HTML Embed'}</p>
                    <p className="text-xs text-stone-500 leading-snug mt-0.5">Embeddable code with animations</p>
                  </div>
                </button>
                <div className="h-px bg-stone-700 mx-3" />
                <button onClick={handleDownloadClean} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-stone-700 transition-colors cursor-pointer">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-700 text-stone-400 shrink-0 mt-0.5">
                    <i className="ri-image-line text-sm" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-stone-300 whitespace-nowrap">Download Clean Image</p>
                    <p className="text-xs text-stone-500 leading-snug mt-0.5">Background only, no overlays</p>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Mobile-only controls ──────────────────────────────── */}
        <div className="flex md:hidden items-center gap-1 ml-auto">
          {/* Add (mobile) — uses its own separate ref and state */}
          <div className="relative" ref={mobileAddRef}>
            <button
              onClick={() => setMobileAddOpen((o) => !o)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors cursor-pointer"
            >
              <i className="ri-add-line text-lg" />
            </button>
            {mobileAddOpen && (
              <div className="absolute top-full right-0 mt-1.5 w-44 bg-stone-800 border border-stone-700 rounded-xl overflow-hidden shadow-xl z-50">
                <button onClick={() => { onAddText(); setMobileAddOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-text text-orange-400" /> Text Block
                </button>
                <button onClick={() => { onAddButton(); setMobileAddOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-cursor-fill text-orange-400" /> CTA Button
                </button>
                <button onClick={() => { onAddShape(); setMobileAddOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-square-line text-orange-400" /> Shape / Box
                </button>
                <div className="h-px bg-stone-700 mx-3" />
                <button onClick={() => { setMobileAddOpen(false); setTimeout(() => logoInputRef.current?.click(), 0); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-image-add-line text-orange-400" /> Logo / Image
                </button>
              </div>
            )}
          </div>

          {/* Undo */}
          <button onClick={onUndo} disabled={!canUndo} className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${canUndo ? 'text-stone-400 hover:bg-stone-800 hover:text-stone-200 cursor-pointer' : 'text-stone-700 cursor-not-allowed'}`}>
            <i className="ri-arrow-go-back-line text-base" />
          </button>

          {/* Redo */}
          <button onClick={onRedo} disabled={!canRedo} className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${canRedo ? 'text-stone-400 hover:bg-stone-800 hover:text-stone-200 cursor-pointer' : 'text-stone-700 cursor-not-allowed'}`}>
            <i className="ri-arrow-go-forward-line text-base" />
          </button>

          {/* More menu */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen((o) => !o)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-stone-800 border border-stone-700 text-stone-400 hover:text-stone-200 transition-colors cursor-pointer"
            >
              <i className="ri-more-2-fill text-base" />
            </button>
            {moreOpen && (
              <div className="absolute top-full right-0 mt-1.5 w-56 bg-stone-800 border border-stone-700 rounded-xl overflow-hidden shadow-2xl z-50">
                {/* Save */}
                {onSave && (
                  <>
                    <button
                      onClick={() => { onSave(); setMoreOpen(false); }}
                      disabled={isSaving}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${isSaving ? 'text-stone-500' : 'text-emerald-400 hover:bg-stone-700'}`}
                    >
                      <i className={isSaving ? 'ri-loader-4-line animate-spin' : 'ri-save-3-line'} />
                      {saveLabel ?? (isSaving ? 'Saving…' : 'Save')}
                    </button>
                    <div className="h-px bg-stone-700 mx-3" />
                  </>
                )}

                {/* Animation */}
                <div className="px-4 py-2">
                  <p className="text-xs text-stone-500 font-semibold uppercase tracking-wider mb-2">Animation</p>
                  <div className="space-y-0.5">
                    {ANIMATION_OPTIONS.map((opt) => (
                      <button key={opt.id} onClick={() => { onAnimationChange(opt.id); setMoreOpen(false); }} className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-xs transition-colors cursor-pointer ${animation === opt.id ? 'bg-orange-500/20 text-orange-300' : 'text-stone-300 hover:bg-stone-700'}`}>
                        <i className={`${opt.icon} ${animation === opt.id ? 'text-orange-400' : 'text-stone-500'}`} />
                        {opt.label}
                        {animation === opt.id && <i className="ri-check-line text-orange-400 ml-auto" />}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-stone-700 mx-3" />

                {/* Change image */}
                <button onClick={() => { imageInputRef.current?.click(); setMoreOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer">
                  <i className="ri-image-edit-line text-orange-400" /> Change Image
                </button>

                {/* Erase tool */}
                {onErase && (
                  <button onClick={() => { setMoreOpen(false); onErase(); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer">
                    <i className="ri-eraser-line text-orange-400" /> Erase Tool
                    <span className="ml-auto px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold leading-none">Beta</span>
                  </button>
                )}

                <div className="h-px bg-stone-700 mx-3" />

                {/* Export options */}
                <button onClick={handleExportVideoClick} disabled={isExportingVideo} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer disabled:opacity-50">
                  <i className={`${isExportingVideo ? 'ri-loader-4-line animate-spin' : 'ri-film-line'} text-orange-400`} />
                  {isExportingVideo ? `Rendering… ${Math.round(videoProgress * 100)}%` : 'Export Video'}
                </button>
                <button onClick={handleExportOverlays} disabled={isExporting} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer disabled:opacity-50">
                  <i className="ri-image-2-line text-orange-400" /> Export PNG
                </button>
                <button onClick={handleExportHtmlClick} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer">
                  <i className={`${htmlCopied ? 'ri-check-line text-green-400' : 'ri-code-s-slash-line text-stone-400'}`} />
                  {htmlCopied ? 'HTML Copied!' : 'Copy HTML Embed'}
                </button>
                <button onClick={handleDownloadClean} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer">
                  <i className="ri-download-2-line text-stone-400" /> Download Clean
                </button>

                {onStartNew && (
                  <>
                    <div className="h-px bg-stone-700 mx-3" />
                    <button
                      onClick={() => { setMoreOpen(false); setShowNewConfirm(true); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-400 hover:bg-stone-700 hover:text-stone-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-restart-line" /> Start New
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Start New confirm (full-width sheet) */}
      {showNewConfirm && (
        <div className="md:hidden bg-stone-800 border-b border-stone-700 px-4 py-3 flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-500/20 shrink-0">
            <i className="ri-alert-line text-amber-400 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-white">Start a new project?</p>
            <p className="text-xs text-stone-400">Canvas &amp; elements will be cleared.</p>
          </div>
          <button onClick={() => { setShowNewConfirm(false); onStartNew?.(); }} className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition-colors cursor-pointer whitespace-nowrap">
            Yes
          </button>
          <button onClick={() => setShowNewConfirm(false)} className="px-3 py-1.5 rounded-lg bg-stone-700 text-stone-300 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
        </div>
      )}

      {/* Export error banner */}
      {exportError && (
        <div className="flex items-center gap-3 px-4 py-2 bg-red-900/60 border-b border-red-800 text-red-300 text-xs">
          <i className="ri-error-warning-line text-red-400 shrink-0" />
          <span className="flex-1">{exportError}</span>
        </div>
      )}

      {/* Video export progress banner */}
      {isExportingVideo && (
        <div className="flex items-center gap-3 px-4 py-2 bg-stone-800 border-b border-stone-700">
          <i className="ri-film-line text-orange-400 text-sm shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-stone-300 font-medium">Rendering animated video…</span>
              <span className="text-xs text-orange-400 font-bold">{Math.round(videoProgress * 100)}%</span>
            </div>
            <div className="h-1.5 bg-stone-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-150" style={{ width: `${videoProgress * 100}%` }} />
            </div>
          </div>
          <span className="text-xs text-stone-500 shrink-0 whitespace-nowrap">Please wait</span>
        </div>
      )}

      {/* HTML Copied toast */}
      {htmlCopied && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-full shadow-lg z-50 whitespace-nowrap">
          <i className="ri-check-line" />
          HTML embed code copied!
        </div>
      )}
    </div>
  );
}
